/*
 * Copyright (c) Nexign, JSC, 2020-2023.
 */
/**
 * Page Tariffs And Services Actions
 *
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Yakov Gushchin
 * @author Evgeniy Naumov
 * @version 1.23.2
 * @since 1.4.0
 */

import { createAction } from '../../modules/app/createAction';
import {
    GET_TARIFFS_AND_SERVICES_REGION_LIST_ACTION,
    GET_TARIFFS_AND_SERVICES_REGION_LIST_FAILURE,
    GET_TARIFFS_AND_SERVICES_REGION_LIST_SUCCESS,
    PRODUCTS_CLEAR_ACTION,
    PRODUCTS_CONNECT_SELECTED_SERVICES,
    PRODUCTS_GET_CATEGORY_LIST_ACTION,
    PRODUCTS_GET_CATEGORY_LIST_FAILURE,
    PRODUCTS_GET_CATEGORY_LIST_SUCCESS,
    PRODUCTS_GET_META_ACTION,
    PRODUCTS_GET_META_FAILURE,
    PRODUCTS_GET_META_SUCCESS,
    PRODUCTS_GET_PRODUCT_LIST_ACTION,
    PRODUCTS_GET_PRODUCT_LIST_AVAILABLE_FAILURE_ACTION,
    PRODUCTS_GET_PRODUCT_LIST_CONNECTED_FAILURE_ACTION,
    PRODUCTS_GET_PRODUCT_LIST_SUCCESS,
    PRODUCTS_INIT_ACTION,
    PRODUCTS_SET_CATEGORY_IDS,
    PRODUCTS_SET_CATEGORY_SELECTED,
    PRODUCTS_SET_FILTER_SELECTION,
    PRODUCTS_SET_PAGE_FROM,
    PRODUCTS_SET_TAB_SELECTED,
    PRODUCTS_START_WIZARD_FROM_SUBSCRIBER_ID,
    SET_TARIFFS_AND_SERVICES_FILTER_REGION_VISIBLE,
    SET_TARIFFS_AND_SERVICES_ITEM_SELECTED,
    SET_TARIFFS_AND_SERVICES_ITEMS_SELECTED,
    SET_TARIFFS_AND_SERVICES_PAYMENT_SELECTED,
    SET_TARIFFS_AND_SERVICES_REGION_DISABLED_ACTION,
    SET_TARIFFS_AND_SERVICES_REGION_SELECTED,
    SET_TARIFFS_AND_SERVICES_SET_TARIFF_RANGE,
    SET_TARIFFS_AND_SERVICES_TARIFF_SELECTED,
    TARIFFS_AND_SERVICES_SEARCH_START,
    TARIFFS_AND_SERVICES_SET_SEARCH_VALUE,
    GET_ROAMING_OPTIONS_COUNTRIES_SEARCH,
    GET_ROAMING_OPTIONS_COUNTRIES_SEARCH_SUCCESS,
    GET_ROAMING_OPTIONS_COUNTRIES_SEARCH_FAILURE,
    SET_SELECTED_COUNTRY_CODE,
    GET_ROAMING_OPTIONS_FOR_COUNTRY,
    GET_ROAMING_OPTIONS_FOR_COUNTRY_SUCCESS,
    GET_ROAMING_OPTIONS_FOR_COUNTRY_FAILURE,
    CLEAR_ROAMING_OPTIONS_SEARCH_ACTION,
    SET_SELECTED_ROAMING_OPTION,
    SET_INCLUDE_CAMPAIGN_SERVICE_PRODUCTS,
    SET_INCLUDE_CAMPAIGN_TARIFF_PRODUCTS,
} from './tariffsAndServicesConstants';
import { IProductCardTariffSetRange } from './tariffsAndServicesProductTariffTypes';
import {
    IGetTariffsAndServicesRegionListSuccessPayload,
    IInitTariffsAndServicesActionPayload,
    IProductCategoryIds,
    IProductsCategory,
    IProductsConnectSelectedServicesPayload,
    IProductsGetProductListSuccessPayload,
    IProductsMeta,
    IProductsSetItemSelectedPayload,
    IProductsSetItemsSelectedPayload,
    IProductsSetPageFromPayload,
    IProductsSetRegionDisabledPayload,
    IProductsSetRegionSelectedPayload,
    IProductsSetTabSelectedPayload,
    IProductStartWizardFromSubscriberIdPayload,
    IRoamingCountriesResponse,
    IRoamingCountrySearch,
    IRoamingOption,
    IRoamingOptions,
    IRoamingOptionsSearch,
    ISelectedCountryCode,
    ISetIncludeCampaignServiceProductsPayload,
    ISetTariffsAndServicesFilterRegionVisiblePayload,
    ISetTariffsAndServicesPaymentSelectedPayload,
    TClearRoamingSearchAction,
    TGetTariffsAndServicesRegionListAction,
    TGetTariffsAndServicesRegionListFailure,
    TGetTariffsAndServicesRegionListSuccess,
    TIncludeCampaignServiceProductsAction,
    TIncludeCampaignTariffProductsAction,
    TInitTariffsAndServicesAction,
    TProductsClearAction,
    TProductsConnectSelectedServicesAction,
    TProductsGetCategoryListAction,
    TProductsGetCategoryListFailure,
    TProductsGetCategoryListSuccess,
    TProductsGetMetaAction,
    TProductsGetMetaFailure,
    TProductsGetMetaSuccess,
    TProductsGetProductListAction,
    TProductsGetProductListAvailableFailureAction,
    TProductsGetProductListConnectedFailureAction,
    TProductsGetProductListSuccess,
    TProductsSetCategorySelectedAction,
    TProductsSetPageFromAction,
    TProductsSetRegionSelectedAction,
    TProductsSetTabSelected,
    TProductStartWizardFromSubscriberIdAction,
    TRoamingOptionsCountriesFailure,
    TRoamingOptionsCountriesSuccess,
    TRoamingOptionsFailure,
    TRoamingOptionsSuccess,
    TSelectedCountryCode,
    TSelectedRoamingOption,
    TSetTariffsAndServicesFilterRegionVisible,
    TSetTariffsAndServicesItemSelected,
    TSetTariffsAndServicesItemsSelected,
    TSetTariffsAndServicesPaymentSelected,
    TSetTariffsAndServicesRegionDisabledAction,
    TSetTariffsAndServicesTariffSelectedAction,
    TSetTariffsAndServicesTariffSetRangeAction,
    TTariffsAndServicesSearchStartAction,
    TTariffsAndServicesSetSearchValueAction,
} from './tariffsAndServicesTypes';

export const initTariffsAndServicesAction = (
    payload: IInitTariffsAndServicesActionPayload,
): TInitTariffsAndServicesAction => createAction(PRODUCTS_INIT_ACTION, payload);

export const productsGetMetaAction = (): TProductsGetMetaAction => createAction(PRODUCTS_GET_META_ACTION);
export const productsGetMetaSuccess = (payload: IProductsMeta): TProductsGetMetaSuccess =>
    createAction(PRODUCTS_GET_META_SUCCESS, payload);
export const productsGetMetaFailure = (): TProductsGetMetaFailure => createAction(PRODUCTS_GET_META_FAILURE);

export const productsGetCategoryListAction = (): TProductsGetCategoryListAction =>
    createAction(PRODUCTS_GET_CATEGORY_LIST_ACTION);
export const productsGetCategoryListSuccess = (payload: IProductsCategory[]): TProductsGetCategoryListSuccess =>
    createAction(PRODUCTS_GET_CATEGORY_LIST_SUCCESS, payload);
export const productsGetCategoryListFailure = (payload: string): TProductsGetCategoryListFailure =>
    createAction(PRODUCTS_GET_CATEGORY_LIST_FAILURE, payload);

export const productsGetServiceListAction = (): TProductsGetProductListAction =>
    createAction(PRODUCTS_GET_PRODUCT_LIST_ACTION);
export const productsGetProductListSuccess = (
    payload: IProductsGetProductListSuccessPayload,
): TProductsGetProductListSuccess => createAction(PRODUCTS_GET_PRODUCT_LIST_SUCCESS, payload);
export const productsGetProductListAvailableFailureAction = (
    payload: string,
): TProductsGetProductListAvailableFailureAction =>
    createAction(PRODUCTS_GET_PRODUCT_LIST_AVAILABLE_FAILURE_ACTION, payload);
export const productsGetProductListConnectedFailureAction = (
    payload: string,
): TProductsGetProductListConnectedFailureAction =>
    createAction(PRODUCTS_GET_PRODUCT_LIST_CONNECTED_FAILURE_ACTION, payload);

export const getTariffsAndServicesRegionListAction = (): TGetTariffsAndServicesRegionListAction =>
    createAction(GET_TARIFFS_AND_SERVICES_REGION_LIST_ACTION);
export const getTariffsAndServicesRegionListSuccess = (
    payload: IGetTariffsAndServicesRegionListSuccessPayload,
): TGetTariffsAndServicesRegionListSuccess => createAction(GET_TARIFFS_AND_SERVICES_REGION_LIST_SUCCESS, payload);
export const getTariffsAndServicesRegionListFailure = (payload: string): TGetTariffsAndServicesRegionListFailure =>
    createAction(GET_TARIFFS_AND_SERVICES_REGION_LIST_FAILURE, payload);

export const setTariffsAndServicesRegionDisabledAction = (
    payload: IProductsSetRegionDisabledPayload,
): TSetTariffsAndServicesRegionDisabledAction => createAction(SET_TARIFFS_AND_SERVICES_REGION_DISABLED_ACTION, payload);

export const tariffsAndServicesSearchStartAction = (): TTariffsAndServicesSearchStartAction =>
    createAction(TARIFFS_AND_SERVICES_SEARCH_START);

export const tariffsAndServicesSetSearchValueAction = (payload: string): TTariffsAndServicesSetSearchValueAction =>
    createAction(TARIFFS_AND_SERVICES_SET_SEARCH_VALUE, payload);

export const productsSetCategorySelectedAction = (payload: number): TProductsSetCategorySelectedAction =>
    createAction(PRODUCTS_SET_CATEGORY_SELECTED, payload);

export const setTariffsAndServicesItemSelected = (
    payload: IProductsSetItemSelectedPayload,
): TSetTariffsAndServicesItemSelected => createAction(SET_TARIFFS_AND_SERVICES_ITEM_SELECTED, payload);
export const setProductsItemsSelectedAction = (
    payload: IProductsSetItemsSelectedPayload,
): TSetTariffsAndServicesItemsSelected => {
    return createAction(SET_TARIFFS_AND_SERVICES_ITEMS_SELECTED, payload);
};
export const setProductsTariffSelectedAction = (
    payload: IProductsSetItemSelectedPayload,
): TSetTariffsAndServicesTariffSelectedAction => createAction(SET_TARIFFS_AND_SERVICES_TARIFF_SELECTED, payload);
export const setTariffAndServicesTariffSetRangeAction = (
    payload: IProductCardTariffSetRange,
): TSetTariffsAndServicesTariffSetRangeAction => createAction(SET_TARIFFS_AND_SERVICES_SET_TARIFF_RANGE, payload);

export const setTariffsAndServicesPaymentSelected = (
    payload: ISetTariffsAndServicesPaymentSelectedPayload,
): TSetTariffsAndServicesPaymentSelected => createAction(SET_TARIFFS_AND_SERVICES_PAYMENT_SELECTED, payload);

export const productsSetTabSelected = (payload: IProductsSetTabSelectedPayload): TProductsSetTabSelected =>
    createAction(PRODUCTS_SET_TAB_SELECTED, payload);

export const productsSetRegionSelected = (
    payload: IProductsSetRegionSelectedPayload,
): TProductsSetRegionSelectedAction => createAction(SET_TARIFFS_AND_SERVICES_REGION_SELECTED, payload);

export const setTariffsAndServicesFilterRegionVisible = (
    payload: ISetTariffsAndServicesFilterRegionVisiblePayload,
): TSetTariffsAndServicesFilterRegionVisible => createAction(SET_TARIFFS_AND_SERVICES_FILTER_REGION_VISIBLE, payload);

export const productsSetPageFromAction = (payload: IProductsSetPageFromPayload): TProductsSetPageFromAction =>
    createAction(PRODUCTS_SET_PAGE_FROM, payload);

export const productsSetFilterSelectionAction = (payload: boolean) =>
    createAction(PRODUCTS_SET_FILTER_SELECTION, payload);

export const productsSetCategoryIdsAction = (payload: IProductCategoryIds) =>
    createAction(PRODUCTS_SET_CATEGORY_IDS, payload);

export const productsConnectSelectedServicesAction = (
    payload: IProductsConnectSelectedServicesPayload,
): TProductsConnectSelectedServicesAction => createAction(PRODUCTS_CONNECT_SELECTED_SERVICES, payload);

export const productsClearAction = (): TProductsClearAction => createAction(PRODUCTS_CLEAR_ACTION);

export const productsStartWizardFromSubscriberIdAction = (
    payload: IProductStartWizardFromSubscriberIdPayload,
): TProductStartWizardFromSubscriberIdAction => createAction(PRODUCTS_START_WIZARD_FROM_SUBSCRIBER_ID, payload);

export const roamingOptionsGetCountriesAction = (
    payload: IRoamingCountrySearch,
) => createAction(GET_ROAMING_OPTIONS_COUNTRIES_SEARCH, payload);

export const roamingOptionsGetCountriesSuccess = (
    payload: IRoamingCountriesResponse,
): TRoamingOptionsCountriesSuccess => createAction(GET_ROAMING_OPTIONS_COUNTRIES_SEARCH_SUCCESS, payload);

export const roamingOptionsGetCountriesFailure = (
    payload: string,
): TRoamingOptionsCountriesFailure => createAction(GET_ROAMING_OPTIONS_COUNTRIES_SEARCH_FAILURE, payload);

export const setSelectedCountryCode = (
    payload: ISelectedCountryCode,
): TSelectedCountryCode => createAction(SET_SELECTED_COUNTRY_CODE, payload);

export const setSelectedRoamingOption = (
    payload: IRoamingOption,
): TSelectedRoamingOption => createAction(SET_SELECTED_ROAMING_OPTION, payload);

export const roamingOptionsGetAction = (
    payload: IRoamingOptionsSearch,
) => createAction(GET_ROAMING_OPTIONS_FOR_COUNTRY, payload);

export const roamingOptionsGetSuccess = (
    payload: IRoamingOptions,
): TRoamingOptionsSuccess => createAction(GET_ROAMING_OPTIONS_FOR_COUNTRY_SUCCESS, payload);

export const roamingOptionsGetFailure = (
    payload: string,
): TRoamingOptionsFailure => createAction(GET_ROAMING_OPTIONS_FOR_COUNTRY_FAILURE, payload);

export const clearRoamingOptionsSearchAction =
    (): TClearRoamingSearchAction => createAction(CLEAR_ROAMING_OPTIONS_SEARCH_ACTION);

export const includeCampaignServiceProductsAction = (
    payload: ISetIncludeCampaignServiceProductsPayload,
): TIncludeCampaignServiceProductsAction => createAction(SET_INCLUDE_CAMPAIGN_SERVICE_PRODUCTS, payload);

export const includeCampaignTariffProductsAction = (
    payload: ISetIncludeCampaignServiceProductsPayload,
): TIncludeCampaignTariffProductsAction => createAction(SET_INCLUDE_CAMPAIGN_TARIFF_PRODUCTS, payload);
